/**
 * Zerion API Integration
 *
 * Fetches DeFi LP positions and token balances for a wallet address
 * using the Zerion portfolio API (JSON:API format).
 *
 * NOTE: The Zerion API may have CORS restrictions when called directly
 * from the browser. If you encounter CORS errors, you will need to
 * proxy requests through the backend (e.g. FastAPI) or use a CORS proxy.
 * For now this module uses standard fetch — add a proxy layer later if needed.
 */

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const ZERION_BASE_URL = 'https://api.zerion.io/v1';
const ZERION_API_KEY = process.env.REACT_APP_ZERION_API_KEY || '';

if (!ZERION_API_KEY) {
  console.warn('[zerionApi] REACT_APP_ZERION_API_KEY not set — wallet analysis will be unavailable');
}

// Base64-encoded "apiKey:" for Basic auth
const AUTH_HEADER = ZERION_API_KEY ? `Basic ${btoa(ZERION_API_KEY + ':')}` : '';

/** Flag the UI can check to know this integration exists */
export const ZERION_SUPPORTED = !!ZERION_API_KEY;

// ---------------------------------------------------------------------------
// Chain name normalisation
// ---------------------------------------------------------------------------

const CHAIN_NAME_MAP = {
  ethereum: 'Ethereum',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  base: 'Base',
  polygon: 'Polygon',
  'binance-smart-chain': 'BSC',
  bsc: 'BSC',
  avalanche: 'Avalanche',
  gnosis: 'Gnosis',
  fantom: 'Fantom',
  zksync: 'zkSync',
  'zksync-era': 'zkSync',
  linea: 'Linea',
  scroll: 'Scroll',
  blast: 'Blast',
};

function normalizeChain(rawChain) {
  if (!rawChain) return 'Unknown';
  const key = String(rawChain).toLowerCase().trim();
  return CHAIN_NAME_MAP[key] || rawChain.charAt(0).toUpperCase() + rawChain.slice(1);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildHeaders() {
  return {
    Authorization: AUTH_HEADER,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

/**
 * Generic fetch wrapper with error handling.
 * Returns parsed JSON or null on failure.
 */
async function zerionFetch(path, params = {}) {
  const url = new URL(`${ZERION_BASE_URL}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: buildHeaders(),
    });

    if (!response.ok) {
      console.error(
        `[zerionApi] Request failed: ${response.status} ${response.statusText} — ${url.pathname}`
      );
      return null;
    }

    return await response.json();
  } catch (err) {
    console.error('[zerionApi] Network error:', err.message || err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Token extraction helpers
// ---------------------------------------------------------------------------

/**
 * Extract token details from a position's attributes.
 * Zerion embeds token info differently depending on the position type;
 * this function tries several known shapes.
 */
function extractTokens(attributes) {
  const tokens = [];

  // 1. fungible_info — single-token positions (staked, locked)
  if (attributes.fungible_info) {
    const fi = attributes.fungible_info;
    tokens.push({
      symbol: fi.symbol || '',
      name: fi.name || '',
      value: attributes.value || 0,
      quantity: attributes.quantity?.float ?? attributes.quantity?.numeric ?? 0,
      iconUrl: fi.icon?.url || null,
    });
  }

  // 2. Implementations / underlying tokens for LP or vault positions
  if (attributes.implementations && Array.isArray(attributes.implementations)) {
    for (const impl of attributes.implementations) {
      if (impl.fungible_info) {
        const fi = impl.fungible_info;
        tokens.push({
          symbol: fi.symbol || '',
          name: fi.name || '',
          value: impl.value || 0,
          quantity: impl.quantity?.float ?? impl.quantity?.numeric ?? 0,
          iconUrl: fi.icon?.url || null,
        });
      }
    }
  }

  // 3. Interpreted fields — some responses include a flat `tokens` array
  if (attributes.tokens && Array.isArray(attributes.tokens)) {
    for (const t of attributes.tokens) {
      tokens.push({
        symbol: t.symbol || t.fungible_info?.symbol || '',
        name: t.name || t.fungible_info?.name || '',
        value: t.value || 0,
        quantity: t.quantity?.float ?? t.quantity?.numeric ?? 0,
        iconUrl: t.icon?.url || t.fungible_info?.icon?.url || null,
      });
    }
  }

  return tokens;
}

// ---------------------------------------------------------------------------
// Position type helpers
// ---------------------------------------------------------------------------

/** Position types we consider "DeFi / LP" (as opposed to simple wallet holdings) */
const DEFI_POSITION_TYPES = new Set(['deposit', 'staked', 'locked', 'reward', 'claimable']);

function isDefiPosition(positionType) {
  return DEFI_POSITION_TYPES.has(positionType);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch DeFi LP / staked / locked positions for a wallet.
 *
 * @param {string} walletAddress - EVM wallet address (0x...)
 * @returns {Promise<Array>} Normalised array of DeFi positions sorted by value desc.
 *   Each item: { type, protocolName, poolName, chain, tokens, totalValueUsd, changes }
 */
export async function fetchWalletPositions(walletAddress) {
  if (!walletAddress) {
    console.warn('[zerionApi] fetchWalletPositions called without an address');
    return [];
  }

  const json = await zerionFetch(`/wallets/${walletAddress}/positions/`, {
    'filter[position_types]': 'deposit,staked,locked',
    currency: 'usd',
    sort: 'value',
  });

  if (!json || !Array.isArray(json.data)) {
    return [];
  }

  const positions = json.data
    .filter((item) => {
      const posType = item.attributes?.position_type;
      return isDefiPosition(posType);
    })
    .map((item) => {
      const attrs = item.attributes || {};
      const changes = attrs.changes || {};

      // Chain can live in relationships or in attributes
      let chain = 'Unknown';
      if (item.relationships?.chain?.data?.id) {
        chain = normalizeChain(item.relationships.chain.data.id);
      } else if (attrs.chain) {
        chain = normalizeChain(attrs.chain);
      }

      return {
        type: attrs.position_type || 'deposit',
        protocolName: attrs.protocol || attrs.dapp?.name || 'Unknown',
        poolName: attrs.name || '',
        chain,
        tokens: extractTokens(attrs),
        totalValueUsd: attrs.value ?? 0,
        changes: {
          absolute_1d: changes.absolute_1d ?? null,
          percent_1d: changes.percent_1d ?? null,
        },
      };
    });

  // Ensure descending value sort (API should already do this, but just in case)
  positions.sort((a, b) => b.totalValueUsd - a.totalValueUsd);

  return positions;
}

/**
 * Fetch simple fungible token balances for a wallet.
 * Useful for showing a wallet overview without DeFi detail.
 *
 * @param {string} walletAddress - EVM wallet address (0x...)
 * @returns {Promise<Array>} Array of token balances sorted by value desc.
 *   Each item: { symbol, name, chain, value, quantity, iconUrl, price, changes }
 */
export async function fetchWalletTokenBalances(walletAddress) {
  if (!walletAddress) {
    console.warn('[zerionApi] fetchWalletTokenBalances called without an address');
    return [];
  }

  const json = await zerionFetch(`/wallets/${walletAddress}/positions/`, {
    'filter[position_types]': 'wallet',
    currency: 'usd',
    sort: 'value',
  });

  if (!json || !Array.isArray(json.data)) {
    return [];
  }

  const balances = json.data.map((item) => {
    const attrs = item.attributes || {};
    const fi = attrs.fungible_info || {};
    const changes = attrs.changes || {};

    let chain = 'Unknown';
    if (item.relationships?.chain?.data?.id) {
      chain = normalizeChain(item.relationships.chain.data.id);
    } else if (attrs.chain) {
      chain = normalizeChain(attrs.chain);
    }

    return {
      symbol: fi.symbol || '',
      name: fi.name || '',
      chain,
      value: attrs.value ?? 0,
      quantity: attrs.quantity?.float ?? attrs.quantity?.numeric ?? 0,
      iconUrl: fi.icon?.url || null,
      price: attrs.price ?? null,
      changes: {
        absolute_1d: changes.absolute_1d ?? null,
        percent_1d: changes.percent_1d ?? null,
      },
    };
  });

  balances.sort((a, b) => b.value - a.value);

  return balances;
}
